import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { videos, VideoItem } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const category = categories.find((c) => c.slug === slug);

  const categoryVideos = useMemo(() => {
    if (!category) return [];
    return videos.filter((v) => v.category === category.slug);
  }, [category]);

  const sortedVideos = useMemo(() => {
    const sorted = [...categoryVideos];
    switch (sortBy) {
      case 'hot':
        return sorted.sort((a, b) => b.views - a.views);
      case 'new':
        return sorted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      case 'rating':
        return sorted.sort((a, b) => b.rating - a.rating);
      default:
        return sorted;
    }
  }, [categoryVideos, sortBy]);

  const totalPages = Math.ceil(sortedVideos.length / pageSize);
  const paginatedVideos = sortedVideos.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSortChange = (sort: 'hot' | 'new' | 'rating') => {
    setSortBy(sort);
    setCurrentPage(1);
  };

  if (!category) {
    return (
      <PageLayout showBreadcrumb={false}>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查找的视频分类不存在。</p>
          <Link
            to="/"
            className="inline-flex items-center px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium text-sm rounded-xl transition-colors cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.xiangrikuishipin.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.xiangrikuishipin.com/category/${category.slug}` },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 向日葵视频`,
    description: category.description,
    url: `https://www.xiangrikuishipin.com/category/${category.slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '向日葵视频',
      url: 'https://www.xiangrikuishipin.com/',
    },
  };

  return (
    <PageLayout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />

      <section className="w-full px-4 md:px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Category Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {category.name}
            </h1>
            <p className="text-foreground-500 text-sm max-w-2xl leading-relaxed">
              {category.description}
            </p>
            <div className="flex items-center gap-2 mt-4 text-xs text-foreground-400">
              <span>共 {categoryVideos.length} 部视频</span>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <div className="inline-flex bg-background-100 rounded-full p-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
              {[
                { key: 'hot' as const, label: '最热', icon: 'ri-fire-line' },
                { key: 'new' as const, label: '最新', icon: 'ri-timer-flash-line' },
                { key: 'rating' as const, label: '评分', icon: 'ri-star-line' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleSortChange(opt.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={opt.icon}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Video Grid */}
          {paginatedVideos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {paginatedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} showCategory />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-video-line text-3xl text-foreground-300"></i>
              </span>
              <p className="text-foreground-500">该分类下暂无视频内容</p>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}